import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { concatLatestFrom } from '@ngrx/operators'
import { Events } from '@ngrx/signals/events'
import { Store } from '@ngrx/store'
import { EMPTY, of } from 'rxjs'
import { exhaustMap, filter, groupBy, map, mapTo, mergeMap, pluck, switchMap } from 'rxjs/operators'

import { truthy } from '@app/core/types/truthy'
import { sendingEvents } from '@app/modules/messaging/components/store/sending.events'
import { CreditsNavigationService } from '@app/modules/navigation/services/credits-navigation.service'
import { ProfileGalleryService } from '@app/modules/profile/services/profile-gallery.service'
import { AccessService } from '@app/services/access.service'
import { MemberService } from '@app/services/member.service'

import * as fromFeature from '.'
import * as fromProfile from '../profile'
import * as fromProfiles from '../profiles'
import type * as fromRoot from '../root'

@Injectable()
export class PhotoAccessEffects {
  private readonly actions$ = inject(Actions)
  private readonly events = inject(Events)
  private readonly memberService = inject(MemberService)
  private readonly accessService = inject(AccessService)
  private readonly creditsNavigationService = inject(CreditsNavigationService)
  private readonly profileGalleryService = inject(ProfileGalleryService)
  private readonly store = inject<Store<fromRoot.State>>(Store)

  readonly clickOnUnlockButton$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromFeature.Actions.tryToUnlockPhotos),
      switchMap(({ profile, source, openGallery, profileSecretAccessPageRedirectParams }) => {
        if (this.memberService.account.token_balance >= profile.costs.unlock_access) {
          return of(
            fromFeature.Actions.unlockPhotos({
              profile,
              source,
              openGallery,
              profileSecretAccessPageRedirectParams: profileSecretAccessPageRedirectParams,
            }),
          )
        }

        this.creditsNavigationService.visitCredits('unlockAccess', { profile_id: profile.id, source })
        return EMPTY
      }),
    ),
  )

  readonly unlockPhotos$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromFeature.Actions.unlockPhotos),
        groupBy(({ profile }) => profile.id),
        mergeMap(groupedCalls =>
          groupedCalls.pipe(
            exhaustMap(({ profile, source, openGallery, profileSecretAccessPageRedirectParams }) =>
              this.accessService.unlock(profile, source, profileSecretAccessPageRedirectParams).then(() => {
                if (openGallery) {
                  const fullProfile = this.store.selectSignal(fromProfiles.Selectors.profileFull(profile.id))()
                  if (fullProfile) {
                    this.profileGalleryService.create(fullProfile, [
                      ...fullProfile.visible_secret_videos,
                      ...fullProfile.visible_secret_photos,
                    ])
                  }
                }
              }),
            ),
          ),
        ),
        mapTo(null), // fix for types inconsistency
      ),
    { dispatch: false },
  )

  readonly granted$ = this.actions$.pipe(ofType(fromProfile.Actions.accessGranted), pluck('id'))
  readonly unlocked$ = this.actions$.pipe(ofType(fromProfile.Actions.albumAccessUnlocked), pluck('id'))

  readonly requestOnGranted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromProfile.Actions.accessGranted),
      pluck('id'),
      concatLatestFrom(id => [this.store.select(fromFeature.Selectors.canCreateRequestAccess(id))]),
      filter(([, canRequest]) => truthy(canRequest)),
      map(([id]) => fromProfile.Actions.accessRequested({ id })),
    ),
  )

  readonly requestOrShareOnFakeMessage$ = createEffect(() =>
    this.events.on(sendingEvents.sendFakeMessageSuccess).pipe(
      map(({ payload }) => payload),
      filter(({ payload }) => payload.request_or_share_access),
      map(({ conversation }) =>
        this.memberService.account.benefactor
          ? fromProfile.Actions.accessRequested({ id: conversation.profile.id })
          : fromProfile.Actions.accessGranted({ id: conversation.profile.id }),
      ),
    ),
  )

  readonly requestOrShareOnRealMessage$ = createEffect(() =>
    this.events.on(sendingEvents.sendRealMessageSuccess).pipe(
      map(({ payload }) => payload),
      filter(({ payload, hadFake }) => payload.request_or_share_access && !hadFake),
      map(({ profile_id: id }) =>
        this.memberService.account.benefactor
          ? fromProfile.Actions.accessRequested({ id })
          : fromProfile.Actions.accessGranted({ id }),
      ),
    ),
  )
}
